import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { Product, ProductCategory, ShopOrderHistory } from "@platform/contracts";
import { api } from "../api";
import "./ProductPicker.css";
import type { NotificationKind } from "./Portal";
import { NumberField } from "./NumberField";
import { formatDate } from "../dates";

const VIEW_KEY = "platform:basket-view";
const ALL = "__all__";

/** The catalogue half of every order-building screen: search, category rail,
 *  quick add, re-order recall and quantity entry. Shared so a client basket, the
 *  staff New order modal and Duplicate order all behave identically on a long
 *  catalogue instead of each growing its own flat product list. */
export function ProductPicker({products,categories,history,quantities,onQuantities,onMessage,statusSlot,compact=false}:{
  products:Product[];
  categories:ProductCategory[];
  history:ShopOrderHistory|null;
  quantities:Record<string,number>;
  onQuantities:(update:(current:Record<string,number>)=>Record<string,number>)=>void;
  onMessage:(message:string,kind?:NotificationKind)=>void;
  statusSlot?:ReactNode;
  /** Renders inside a modal: the catalogue gets its own scroll area. */
  compact?:boolean;
}){
  const[search,setSearch]=useState("");const[group,setGroup]=useState(ALL);const[chosenOnly,setChosenOnly]=useState(false);const[usualOnly,setUsualOnly]=useState(false);
  const[view,setView]=useState<"cards"|"list">(()=>localStorage.getItem(VIEW_KEY)==="cards"?"cards":"list");
  const[quickAdd,setQuickAdd]=useState("");const[quickQuantity,setQuickQuantity]=useState(1);
  const catalogue=useRef<HTMLDivElement>(null);const quickAddInput=useRef<HTMLInputElement>(null);
  const usual=useMemo(()=>new Map((history?.items??[]).map((item)=>[item.productId,item])),[history]);
  const chosenCount=products.filter((product)=>(quantities[product.id]??0)>0).length;
  const usualCount=products.filter((product)=>usual.has(product.id)).length;

  const setView2=(next:"cards"|"list")=>{setView(next);localStorage.setItem(VIEW_KEY,next)};
  const groupOf=(product:Product)=>product.featured?"Featured":(product.categoryName||"Uncategorized");
  const groupOrder=(name:string)=>name==="Featured"?-1:name==="Uncategorized"?999:categories.findIndex((item)=>item.name===name);
  // Everything except the category choice, so the rail can show how many products
  // each category still offers under the current search.
  const searched=useMemo(()=>products.filter((product)=>
    (!search||`${product.sku} ${product.name} ${product.description??""}`.toLowerCase().includes(search.toLowerCase()))
    &&(!chosenOnly||(quantities[product.id]??0)>0)
    &&(!usualOnly||usual.has(product.id))
  ),[products,search,chosenOnly,usualOnly,quantities,usual]);
  const rail=useMemo(()=>{
    const counts=new Map<string,{total:number;chosen:number}>();
    for(const product of searched){
      const key=groupOf(product);const entry=counts.get(key)??{total:0,chosen:0};
      entry.total++;if((quantities[product.id]??0)>0)entry.chosen++;counts.set(key,entry);
    }
    return [...counts.entries()].sort((a,b)=>groupOrder(a[0])-groupOrder(b[0]));
  },[searched,quantities,categories]);
  const grouped=useMemo(()=>{
    const buckets=new Map<string,Product[]>();
    for(const product of searched.filter((product)=>group===ALL||groupOf(product)===group)){
      const key=groupOf(product);const bucket=buckets.get(key);
      if(bucket)bucket.push(product);else buckets.set(key,[product]);
    }
    return [...buckets.entries()].sort((a,b)=>groupOrder(a[0])-groupOrder(b[0]));
  },[searched,group,categories]);

  // Adds to whatever is already selected rather than replacing it, so re-ordering
  // last week's list can never wipe work already done today.
  function fillFromLastOrder(){
    if(!history?.lastOrderItems.length)return;
    const available=history.lastOrderItems.filter((item)=>products.some((product)=>product.id===item.productId));
    onQuantities((current)=>{const next={...current};for(const item of available)next[item.productId]=item.quantity;return next});
    const skipped=history.lastOrderItems.length-available.length;
    onMessage(`${available.length} product${available.length===1?"":"s"} from ${history.lastOrderNumber} added.${skipped?` ${skipped} item${skipped===1?" is":"s are"} no longer available.`:""}`);
  }

  function submitQuickAdd(){
    const term=quickAdd.trim().toLowerCase();if(!term)return;
    const partial=products.filter((product)=>product.name.toLowerCase().includes(term));
    const match=products.find((product)=>product.sku.toLowerCase()===term)
      ??products.find((product)=>product.name.toLowerCase()===term)
      ??(partial.length===1?partial[0]:undefined);
    if(!match){onMessage(partial.length>1?`“${quickAdd.trim()}” matches ${partial.length} products — use the full SKU.`:`No product in this catalogue matches “${quickAdd.trim()}”.`,"error");return}
    onQuantities((current)=>({...current,[match.id]:(current[match.id]??0)+quickQuantity}));
    onMessage(`${quickQuantity} × ${match.name} added.`);
    setQuickAdd("");setQuickQuantity(1);quickAddInput.current?.focus();
  }

  // Enter walks down the quantity column and Shift+Enter walks back up, so a long
  // list can be filled in without reaching for the mouse. Arrow keys are left
  // alone because a number input already uses them to step the value.
  function walkQuantities(event:KeyboardEvent<HTMLDivElement>){
    if(event.key!=="Enter")return;
    const target=event.target as HTMLElement;
    if(target.tagName!=="INPUT"||(target as HTMLInputElement).type!=="number")return;
    event.preventDefault();
    const inputs=[...(catalogue.current?.querySelectorAll<HTMLInputElement>('input[type="number"]')??[])];
    const next=inputs[inputs.indexOf(target as HTMLInputElement)+(event.shiftKey?-1:1)];
    if(next){next.focus();next.select()}
  }

  function productCard(product:Product){
    const hint=usual.get(product.id);
    return <article key={product.id} className={(quantities[product.id]??0)>0?"in-basket":""}>
      {product.imageKey&&<img className="product-thumb" src={api.productImageUrl(product.id,product.imageKey)!} alt="" loading="lazy"/>}
      <div><strong>{product.name}{product.featured&&<span className="featured-badge">Featured</span>}</strong><small>€{(product.unitPriceCents/100).toFixed(2)} / {product.unit}{hint&&` · last ordered ${hint.lastQuantity}`}</small></div>
      <NumberField label={`Quantity for ${product.name}`} value={quantities[product.id]??0} onChange={(next)=>onQuantities((current)=>({...current,[product.id]:next}))}/>
    </article>;
  }
  function productRow(product:Product){
    const hint=usual.get(product.id);const quantity=quantities[product.id]??0;
    return <div key={product.id} className={quantity>0?"basket-row in-basket":"basket-row"}>
      <span className="row-name"><strong>{product.name}</strong><small>{product.sku}{product.featured?" · Featured":""}</small></span>
      <span className="row-usual">{hint?<button type="button" className="text-button" title={`Ordered in ${hint.orderCount} of the last ${history?.ordersConsidered} orders`} onClick={()=>onQuantities((current)=>({...current,[product.id]:hint.lastQuantity}))}>last {hint.lastQuantity}</button>:null}</span>
      <span className="row-price">€{(product.unitPriceCents/100).toFixed(2)}<small>/{product.unit}</small></span>
      <NumberField label={`Quantity for ${product.name}`} value={quantity} onChange={(next)=>onQuantities((current)=>({...current,[product.id]:next}))}/>
      <strong className="row-total">{quantity>0?`€${((product.unitPriceCents*quantity)/100).toFixed(2)}`:""}</strong>
    </div>;
  }

  return <div className={compact?"product-picker in-modal":"product-picker"}>
    <div className="picker-tools">
      <input value={search} onChange={(event)=>setSearch(event.target.value)} placeholder="Search by name, SKU or description" aria-label="Search products"/>
      <div className="view-toggle" role="group" aria-label="Catalogue layout">
        <button type="button" className={view==="list"?"active":""} aria-pressed={view==="list"} onClick={()=>setView2("list")}>List</button>
        <button type="button" className={view==="cards"?"active":""} aria-pressed={view==="cards"} onClick={()=>setView2("cards")}>Cards</button>
      </div>
    </div>
    <div className="quick-add">
      <label className="field field-wide"><span>Quick add</span><input ref={quickAddInput} value={quickAdd} onChange={(event)=>setQuickAdd(event.target.value)} onKeyDown={(event)=>{if(event.key==="Enter"){event.preventDefault();submitQuickAdd()}}} placeholder="Scan or type a SKU, then press Enter" aria-label="Quick add by SKU"/></label>
      <NumberField label="Quick add quantity" min={1} value={quickQuantity} onChange={setQuickQuantity}/>
      <button type="button" className="secondary-button" onClick={submitQuickAdd} disabled={!quickAdd.trim()}>Add</button>
    </div>
    {history?.lastOrderNumber&&<div className="reorder-bar"><span>Last order <strong>{history.lastOrderNumber}</strong> on {formatDate(history.lastOrderedAt)} · {history.lastOrderItems.length} products</span><button type="button" className="secondary-button" onClick={fillFromLastOrder}>Fill from last order</button></div>}
    <div className="category-rail" role="group" aria-label="Category filter">
      <button type="button" className={group===ALL?"active":""} aria-pressed={group===ALL} onClick={()=>setGroup(ALL)}>All<small>{searched.length}</small></button>
      {rail.map(([name,counts])=><button type="button" key={name} className={group===name?"active":""} aria-pressed={group===name} onClick={()=>setGroup(name)}>{name}<small>{counts.total}</small>{counts.chosen>0&&<span className="rail-badge" title={`${counts.chosen} in basket`}>{counts.chosen}</span>}</button>)}
    </div>
    <div className="basket-filters">
      <label className="checkbox-field"><input type="checkbox" checked={chosenOnly} onChange={(event)=>setChosenOnly(event.target.checked)} disabled={!chosenCount}/> In basket only</label>
      <label className="checkbox-field"><input type="checkbox" checked={usualOnly} onChange={(event)=>setUsualOnly(event.target.checked)} disabled={!usualCount}/> Usual items{usualCount?` (${usualCount})`:""}</label>
      {(search||group!==ALL||chosenOnly||usualOnly)&&<button type="button" className="text-button" onClick={()=>{setSearch("");setGroup(ALL);setChosenOnly(false);setUsualOnly(false)}}>Reset filters</button>}
      {statusSlot}
    </div>
    <div ref={catalogue} onKeyDown={walkQuantities}>
      {grouped.length===0&&<p className="empty-state">{chosenOnly?"Nothing in this basket yet.":usualOnly?"No previously ordered products match this search.":"No products match this search."}</p>}
      {grouped.map(([groupName,items])=><div className="basket-group" key={groupName}>
        <h4 className="basket-group-heading">{groupName}<small>{items.length} product{items.length===1?"":"s"}</small></h4>
        {view==="cards"?<div className="basket-products">{items.map(productCard)}</div>:<div className="basket-rows">{items.map(productRow)}</div>}
      </div>)}
    </div>
  </div>;
}
